import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { clsx } from 'clsx';
import type { TallerSubscriptionContext } from '../../lib/subscription';
import { getOnboardingProgress, isBillingRestrictedStatus } from '../../lib/subscription';

export function OnboardingProgressStrip({ context }: { context: TallerSubscriptionContext | null | undefined }) {
  if (!context) return null;
  if (isBillingRestrictedStatus(context.status)) return null;

  const onboarding = getOnboardingProgress(context);
  if (onboarding.isCompleted) return null;

  const currentStep = onboarding.steps.find((step) => step.isCurrent) || onboarding.steps.find((step) => !step.completed);
  if (!currentStep) return null;

  return (
    <div className="border-b border-emerald-200 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.18),_transparent_45%),linear-gradient(135deg,_#f0fdf4,_#ecfeff)] px-4 py-4 lg:px-8">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-emerald-700">Onboarding guiado</p>
            <h3 className="mt-1 text-lg font-semibold text-slate-950">Deja tu taller listo en menos de 5 minutos</h3>
            <p className="mt-1 max-w-3xl text-sm text-slate-600">{currentStep.description}</p>
          </div>
        </div>

        <div className="flex flex-col gap-3 xl:min-w-[320px] xl:items-end">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-700">
            <span className="rounded-full border border-emerald-200 bg-white/80 px-3 py-1">
              Paso {Math.min(onboarding.completedSteps + 1, onboarding.totalSteps)} de {onboarding.totalSteps}
            </span>
            <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1">
              {onboarding.progressPercent}% completado
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {onboarding.steps.map((step) => (
              <div
                key={step.key}
                className={clsx(
                  'h-2.5 w-16 rounded-full transition-colors',
                  step.completed ? 'bg-emerald-500' : step.isCurrent ? 'bg-emerald-200' : 'bg-white'
                )}
              />
            ))}
          </div>
          <Link
            to={onboarding.nextActionHref}
            className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            {onboarding.nextActionLabel}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="mt-4 grid gap-2 md:grid-cols-4">
        {onboarding.steps.map((step) => (
          <div
            key={step.key}
            className={clsx(
              'rounded-2xl border px-3 py-3 text-sm',
              step.completed
                ? 'border-emerald-200 bg-white/90 text-emerald-900'
                : step.isCurrent
                  ? 'border-slate-300 bg-white text-slate-900'
                  : 'border-transparent bg-white/60 text-slate-500'
            )}
          >
            <div className="flex items-center gap-2">
              {step.completed ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              ) : (
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">
                  {step.index + 1}
                </span>
              )}
              <p className="font-medium">{step.title}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
